import { getSharedEngine, type AnalysisSnapshot, type EngineWorkerFactory, type LocalEngine } from './local-engine';

/**
 * Shares the one browser engine between every component analysing the board.
 *
 * `LocalEngine` runs one search at a time and each `analyse` cancels the previous
 * one, so two components following the same position (the eval bar wants the best
 * line, move suggestions want the top five) would otherwise cancel each other on
 * every move and one of them would never get a result. Instead, subscribers to a
 * position are served by a single search at the deepest depth and widest MultiPV any
 * of them asked for, and every snapshot is broadcast to all of them.
 *
 * Finished searches are kept in a small in-memory cache, so stepping back and forth
 * through a line does not re-search positions already analysed. Like every local
 * result, cached snapshots stay in this tab and are never sent anywhere (ADR-0015).
 */

type Engine = Pick<LocalEngine, 'analyse' | 'stop'>;

export interface AnalysisSubscriber {
  fen: string;
  depth: number;
  multiPv: number;
  onSnapshot: (snapshot: AnalysisSnapshot) => void;
  onError: (cause: unknown) => void;
}

interface CachedAnalysis {
  snapshot: AnalysisSnapshot;
  /** What the search was asked for; a terminal position completes at depth 0. */
  depth: number;
  multiPv: number;
}

interface RunningSearch {
  engine: Engine;
  fen: string;
  depth: number;
  multiPv: number;
  token: number;
  latest: AnalysisSnapshot | null;
}

/** Positions kept after a finished search; a few long lines' worth. */
const CACHE_SIZE = 128;

export class AnalysisHub {
  private readonly getEngine: () => Engine;
  private readonly cacheSize: number;
  /** Insertion order: the most recent subscriber's position is searched first. */
  private readonly subscribers: AnalysisSubscriber[] = [];
  private readonly cache = new Map<string, CachedAnalysis>();
  private running: RunningSearch | null = null;
  private token = 0;
  private reconcileQueued = false;

  constructor(getEngine: () => Engine, cacheSize = CACHE_SIZE) {
    this.getEngine = getEngine;
    this.cacheSize = cacheSize;
  }

  subscribe(subscriber: AnalysisSubscriber): () => void {
    this.subscribers.push(subscriber);

    // Serve what is already known straight away: a cached result, or the search
    // another component already has running for this position.
    const cached = this.cache.get(subscriber.fen);
    if (cached !== undefined && satisfies(cached, subscriber)) {
      subscriber.onSnapshot(cached.snapshot);
    } else if (this.running?.fen === subscriber.fen && this.running.latest !== null) {
      subscriber.onSnapshot(this.running.latest);
    }

    this.queueReconcile();
    return () => {
      const index = this.subscribers.indexOf(subscriber);
      if (index !== -1) this.subscribers.splice(index, 1);
      this.queueReconcile();
    };
  }

  /**
   * Defer to a microtask so a navigation — which unsubscribes every consumer of the
   * old position and subscribes them to the new one in the same React commit — is
   * settled as one change and starts exactly one search.
   */
  private queueReconcile(): void {
    if (this.reconcileQueued) return;
    this.reconcileQueued = true;
    queueMicrotask(() => {
      this.reconcileQueued = false;
      this.reconcile();
    });
  }

  private reconcile(): void {
    const engine = this.getEngine();
    // The shared engine was replaced (reset/teardown): its search can never settle.
    if (this.running !== null && this.running.engine !== engine) this.running = null;

    const target = this.nextTarget();
    if (target === null) {
      if (this.running !== null) {
        this.running = null;
        engine.stop();
      }
      return;
    }

    const running = this.running;
    if (
      running !== null &&
      running.fen === target.fen &&
      running.depth >= target.depth &&
      running.multiPv >= target.multiPv
    ) {
      return;
    }
    this.start(engine, target);
  }

  /** The most recently requested position the cache cannot already answer. */
  private nextTarget(): { fen: string; depth: number; multiPv: number } | null {
    const seen = new Set<string>();
    for (let index = this.subscribers.length - 1; index >= 0; index -= 1) {
      const fen = this.subscribers[index].fen;
      if (seen.has(fen)) continue;
      seen.add(fen);
      const group = this.subscribers.filter((subscriber) => subscriber.fen === fen);
      const need = {
        fen,
        depth: Math.max(...group.map((subscriber) => subscriber.depth)),
        multiPv: Math.max(...group.map((subscriber) => subscriber.multiPv)),
      };
      const cached = this.cache.get(fen);
      if (cached === undefined || !satisfies(cached, need)) return need;
    }
    return null;
  }

  private start(engine: Engine, target: { fen: string; depth: number; multiPv: number }): void {
    const token = ++this.token;
    const search: RunningSearch = { engine, ...target, token, latest: null };
    this.running = search;

    const current = () => this.running?.token === token;
    engine
      .analyse(target.fen, {
        depth: target.depth,
        multiPv: target.multiPv,
        onUpdate: (snapshot) => {
          if (!current()) return;
          search.latest = snapshot;
          this.broadcast(target.fen, snapshot);
        },
      })
      .then((final) => {
        if (!current()) return;
        this.running = null;
        if (final.complete) {
          this.remember(target.fen, { snapshot: final, depth: target.depth, multiPv: target.multiPv });
        }
        this.broadcast(target.fen, final);
        // Another position may be waiting behind this one.
        this.queueReconcile();
      })
      .catch((cause: unknown) => {
        if (!current()) return;
        this.running = null;
        for (const subscriber of this.subscribersOf(target.fen)) subscriber.onError(cause);
      });
  }

  private broadcast(fen: string, snapshot: AnalysisSnapshot): void {
    for (const subscriber of this.subscribersOf(fen)) subscriber.onSnapshot(snapshot);
  }

  private subscribersOf(fen: string): AnalysisSubscriber[] {
    return this.subscribers.filter((subscriber) => subscriber.fen === fen);
  }

  private remember(fen: string, entry: CachedAnalysis): void {
    this.cache.delete(fen);
    this.cache.set(fen, entry);
    while (this.cache.size > this.cacheSize) {
      const oldest = this.cache.keys().next().value;
      if (oldest === undefined) break;
      this.cache.delete(oldest);
    }
  }
}

function satisfies(cached: CachedAnalysis, need: { depth: number; multiPv: number }): boolean {
  return cached.depth >= need.depth && cached.multiPv >= need.multiPv;
}

let sharedHub: AnalysisHub | null = null;

/** The hub in front of `getSharedEngine`; one per tab, like the engine. */
export function getSharedHub(factory?: EngineWorkerFactory): AnalysisHub {
  if (sharedHub === null) sharedHub = new AnalysisHub(() => getSharedEngine(factory));
  return sharedHub;
}

/** Forget the hub and its cache (tests). */
export function resetSharedHub(): void {
  sharedHub = null;
}
